export const thinkingMessages = [
  "Thinking...",
  "Hallucinating responsibly...",
  "Installing malware on your device - just kidding! :) ",
] as const;

export type ThinkingMessage = (typeof thinkingMessages)[number];

export type ThinkingMessageDeck = {
  lastMessage?: ThinkingMessage;
  remaining: ThinkingMessage[];
};

function isThinkingMessage(value: unknown): value is ThinkingMessage {
  return typeof value === "string" && thinkingMessages.includes(value as ThinkingMessage);
}

function shuffle(messages: ThinkingMessage[], random: () => number): ThinkingMessage[] {
  const shuffled = [...messages];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
}

function normalizeDeck({ lastMessage, remaining }: ThinkingMessageDeck): ThinkingMessageDeck {
  const seen = new Set<ThinkingMessage>();
  const normalizedRemaining = remaining.filter((message) => {
    if (!isThinkingMessage(message) || message === lastMessage || seen.has(message)) return false;
    seen.add(message);
    return true;
  });

  return {
    lastMessage: isThinkingMessage(lastMessage) ? lastMessage : undefined,
    remaining: normalizedRemaining,
  };
}

export function takeNextThinkingMessage(
  deck: ThinkingMessageDeck,
  random: () => number = Math.random,
): { message: ThinkingMessage; deck: ThinkingMessageDeck } {
  const normalizedDeck = normalizeDeck(deck);
  let remaining = normalizedDeck.remaining;

  if (!remaining.length) {
    remaining = shuffle([...thinkingMessages], random);
    if (normalizedDeck.lastMessage && remaining[0] === normalizedDeck.lastMessage) {
      [remaining[0], remaining[1]] = [remaining[1], remaining[0]];
    }
  }

  const [message, ...nextRemaining] = remaining;
  return {
    message,
    deck: {
      lastMessage: message,
      remaining: nextRemaining,
    },
  };
}
